import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { MeetingNoteTypes } from '../MeetingNoteTypes';
import { getFormLabelStyle, getFormSelectContainerStyle, getFormSelectStyle } from '../../styles/designSystem';
import { createSafeCloseHandler } from '../../utils/modalUtils';

interface AddMeetingNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddItem: (itemData: any) => Promise<void>;
  projectData: ProjectInterface;
  token?: string;
}

interface MeetingNoteFormData {
  notes: string;
  customers: string; // Single user ID from project team
  vendors: string; // Single user ID from project team
  date: string;
  meeting_type_id: string;
}

export const AddMeetingNoteModal: React.FC<AddMeetingNoteModalProps> = ({
  isOpen,
  onClose,
  onAddItem,
  projectData,
  token
}) => {
  // const { keycloak: { idTokenParsed } } = useKeycloak();
  
  const [formData, setFormData] = useState<MeetingNoteFormData>({
    notes: '',
    customers: '',
    vendors: '',
    date: new Date().toISOString().split('T')[0],
    meeting_type_id: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.date) {
      toast.error('Date is required');
      return;
    }

    setIsLoading(true);
    try {
      const itemData = {
        project_id: projectData.id,
        notes: formData.notes,
        // Convert single user IDs to arrays (API expects arrays)
        customers: formData.customers ? [formData.customers] : [],
        vendors: formData.vendors ? [formData.vendors] : [],
        date: formData.date ? new Date(formData.date).toISOString() : new Date().toISOString(),
        meeting_type_id: formData.meeting_type_id || '',
      };
      
      console.log('AddMeetingNoteModal: submitting item data', itemData);
      
      await onAddItem(itemData);
      
      console.log('AddMeetingNoteModal: meeting note added successfully');
      toast.success('Meeting note added successfully');
      
      // Reset form
      setFormData({
        notes: '',
        customers: '',
        vendors: '',
        date: new Date().toISOString().split('T')[0],
        meeting_type_id: ''
      });
      
      onClose();
    } catch (error) {
      console.error('AddMeetingNoteModal: failed to add meeting note', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to add meeting note';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Create safe close handler
  const handleSafeClose = createSafeCloseHandler(
    formData, 
    onClose, 
    ['date'] // Exclude auto-filled fields
  );

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-white bg-opacity-80 flex items-center justify-center z-50"
      onClick={handleSafeClose}
    >
      <button
        onClick={() => handleSafeClose()}
        className="fixed top-4 right-4 z-[60] text-gray-400 hover:text-gray-600 p-2"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      
      <div 
        className="bg-white rounded-lg border border-gray-200 shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Add Meeting Note</h2>
          <p className="text-sm text-gray-500 mt-1">Create a new meeting note for this project</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Date and Meeting Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block mb-2" style={getFormLabelStyle()}>
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                className="w-full text-sm bg-white border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block mb-2" style={getFormLabelStyle()}>
                Meeting Type
              </label>
              <div style={getFormSelectContainerStyle()}>
                <MeetingNoteTypes
                  onChange={(selectedOption: any) => {
                    const value = selectedOption?.value || '';
                    setFormData(prev => ({ ...prev, meeting_type_id: value }));
                  }}
                  project_id={projectData.id || ''}
                  defaultValue={formData.meeting_type_id}
                  onCreate={(value: string) => setFormData(prev => ({ ...prev, meeting_type_id: value }))}
                  token={token}
                />
              </div>
              </div>
            </div>

          {/* Attendees */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block mb-2" style={getFormLabelStyle()}>
                Customer Attendee
              </label>
              <div style={getFormSelectContainerStyle()}>
                <select
                name="customers"
                value={formData.customers}
                onChange={handleInputChange}
                  style={getFormSelectStyle()}
                >
                  <option value="">Select customer attendee</option>
                  {projectData?.users?.map((user: any) => (
                    <option key={user.id || user.user_id} value={user.id || user.user_id}>
                      {(user.user?.first_name || user.first_name)} {(user.user?.last_name || user.last_name)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block mb-2" style={getFormLabelStyle()}>
                Vendor Attendee
              </label>
              <div style={getFormSelectContainerStyle()}>
                <select
                name="vendors"
                value={formData.vendors}
                onChange={handleInputChange}
                  style={getFormSelectStyle()}
                >
                  <option value="">Select vendor attendee</option>
                  {projectData?.users?.map((user: any) => (
                    <option key={user.id || user.user_id} value={user.id || user.user_id}>
                      {(user.user?.first_name || user.first_name)} {(user.user?.last_name || user.last_name)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            </div>

          {/* Meeting Notes */}
          <div>
              <label className="block mb-2" style={getFormLabelStyle()}>
                Meeting Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={6}
              className="w-full text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                placeholder="Enter meeting notes, discussions, decisions, and action items..."
              />
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Adding...' : 'Add Meeting Note'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 